import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import {
  addMiscExpense,
  deleteMiscExpense,
  getEmployeeMasters,
  getExpenseMasters,
  getMiscExpenses,
  updateMiscExpense,
} from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";

const SALARY_TITLE = "Salary";

function makeDefaultForm() {
  return {
    date: new Date().toISOString().slice(0, 10),
    title: "",
    employeeName: "",
    amount: "",
    notes: "",
  };
}

function formatMoney(value) {
  const num = Number(value) || 0;
  return num.toFixed(2);
}

export default function MiscExpenses({ category = "Delivery", title = "Delivery Expenses" }) {
  const [form, setForm] = useState(() => makeDefaultForm());
  const [editingId, setEditingId] = useState("");
  const [entries, setEntries] = useState([]);
  const [expenseMasters, setExpenseMasters] = useState([]);
  const [employeeMasters, setEmployeeMasters] = useState([]);
  const [query, setQuery] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});
  const isMasterCategory = String(category || "").trim().toLowerCase() === "master";
  const isSalarySelected = isMasterCategory && String(form.title || "").trim().toLowerCase() === SALARY_TITLE.toLowerCase();

  const refresh = async () => {
    setLoading(true);
    try {
      const [expenseList, masterList, employeeList] = await Promise.all([
        getMiscExpenses(),
        getExpenseMasters(),
        getEmployeeMasters(),
      ]);
      const sortedMasters = Array.isArray(masterList)
        ? [...masterList]
            .filter((entry) => String(entry?.name || "").trim().toLowerCase() !== SALARY_TITLE.toLowerCase())
            .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")))
        : [];
      const sortedEmployees = Array.isArray(employeeList)
        ? [...employeeList].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")))
        : [];
      setEntries(Array.isArray(expenseList) ? expenseList : []);
      setExpenseMasters(sortedMasters);
      setEmployeeMasters(sortedEmployees);
    } catch (err) {
      console.error(err);
      alert(`Failed to load ${title.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, title]);

  useEffect(() => {
    setForm(makeDefaultForm());
    setEditingId("");
    setQuery("");
    setDateFromFilter("");
    setDateToFilter("");
  }, [category]);

  const resetForm = () => {
    setForm(makeDefaultForm());
    setEditingId("");
  };

  const expenseNameOptions = useMemo(() => {
    const names = new Set(
      (expenseMasters || [])
        .map((entry) => String(entry?.name || "").trim())
        .filter(Boolean)
    );
    if (isMasterCategory) {
      names.add(SALARY_TITLE);
    }
    if (form.title && !names.has(form.title)) {
      names.add(form.title);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [expenseMasters, form.title, isMasterCategory]);

  const employeeNameOptions = useMemo(() => {
    const names = new Set(
      (employeeMasters || [])
        .map((entry) => String(entry?.name || "").trim())
        .filter(Boolean)
    );
    if (form.employeeName && !names.has(form.employeeName)) {
      names.add(form.employeeName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [employeeMasters, form.employeeName]);

  const hasSelectableExpenseNames = expenseNameOptions.length > 0;
  const hasEmployeeOptions = employeeNameOptions.length > 0;
  const canSubmit = hasSelectableExpenseNames && (!isSalarySelected || hasEmployeeOptions);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const date = String(form.date || "").trim();
    const expenseTitle = String(form.title || "").trim();
    const employeeName = String(form.employeeName || "").trim();
    const amount = Number(form.amount);
    const notes = String(form.notes || "").trim();

    if (!hasSelectableExpenseNames) {
      alert(isMasterCategory ? "No expense options available." : "Add expense names in Expense Master first.");
      return;
    }
    if (!date || !expenseTitle) {
      alert("Date and expense title are required.");
      return;
    }
    if (isMasterCategory && expenseTitle === SALARY_TITLE && !employeeName) {
      alert("Employee name is required for salary entries.");
      return;
    }
    if (!amount || amount <= 0) {
      alert("Enter a valid amount.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date,
        title: expenseTitle,
        employeeName: isMasterCategory && expenseTitle === SALARY_TITLE ? employeeName : "",
        category,
        amount,
        notes,
      };
      if (editingId) {
        await updateMiscExpense(editingId, payload);
      } else {
        await addMiscExpense(payload);
      }
      await refresh();
      resetForm();
    } catch (err) {
      console.error(err);
      alert(err?.message || `Failed to save ${title.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    const fromDate = toComparableYmd(dateFromFilter);
    const toDate = toComparableYmd(dateToFilter);
    return (entries || []).filter((entry) => {
      const entryCategory = String(entry?.category || "Delivery").trim().toLowerCase();
      const entryDate = toComparableYmd(entry?.date);
      if (entryCategory !== String(category).trim().toLowerCase()) return false;
      if (fromDate && (!entryDate || entryDate < fromDate)) return false;
      if (toDate && (!entryDate || entryDate > toDate)) return false;
      if (!q) return true;
      return (
        String(entry?.title || "").toLowerCase().includes(q) ||
        String(entry?.employeeName || "").toLowerCase().includes(q) ||
        String(entry?.notes || "").toLowerCase().includes(q)
      );
    });
  }, [entries, query, dateFromFilter, dateToFilter, category]);

  const totals = useMemo(() => {
    const totalAmount = filteredEntries.reduce((sum, entry) => sum + (Number(entry?.amount) || 0), 0);
    const dates = new Set(filteredEntries.map((entry) => String(entry?.date || "")).filter(Boolean));
    return {
      totalAmount: +totalAmount.toFixed(2),
      count: filteredEntries.length,
      dateCount: dates.size,
    };
  }, [filteredEntries]);

  const dateWiseTotals = useMemo(() => {
    const map = new Map();
    for (const entry of filteredEntries) {
      const date = String(entry?.date || "").trim();
      if (!date) continue;
      if (!map.has(date)) {
        map.set(date, { date, amount: 0, count: 0 });
      }
      const row = map.get(date);
      row.amount += Number(entry?.amount) || 0;
      row.count += 1;
    }
    return Array.from(map.values())
      .map((row) => ({ ...row, amount: +Number(row.amount || 0).toFixed(2) }))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [filteredEntries]);

  const startEdit = (entry) => {
    if (!entry?.id) return;
    setEditingId(entry.id);
    setForm({
      date: String(entry.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      title: String(entry.title || ""),
      employeeName: String(entry.employeeName || ""),
      amount: Number(entry.amount) || "",
      notes: String(entry.notes || ""),
    });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm(`Delete expense "${entry.title || "entry"}"?`)) return;
    setDeleting((prev) => ({ ...prev, [entry.id]: true }));
    try {
      await deleteMiscExpense(entry.id);
      if (editingId === entry.id) {
        resetForm();
      }
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || `Failed to delete ${title.toLowerCase()}.`);
    } finally {
      setDeleting((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    }
  };

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">{title}</h2>
          <span className="entry-count">{editingId ? "Edit Mode" : "Date-wise Entry"}</span>
        </div>
        <form className="inward-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="field fl-field">
              <input
                id="misc-date"
                className="fl-input input-field"
                type="date"
                value={form.date}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="misc-date" className="fl-label">
                Expense Date
              </label>
            </div>

            <div className="field fl-field">
              <select
                id="misc-title"
                className="fl-input input-field"
                value={form.title}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                    employeeName: e.target.value === SALARY_TITLE ? prev.employeeName : "",
                  }))
                }
                style={{ paddingTop: "14px" }}
                required
                disabled={!hasSelectableExpenseNames}
              >
                <option value="">
                  {hasSelectableExpenseNames ? "Select expense name" : "Add Expense Master first"}
                </option>
                {expenseNameOptions.map((expenseName) => (
                  <option key={expenseName} value={expenseName}>
                    {expenseName}
                  </option>
                ))}
              </select>
              <label htmlFor="misc-title" className="fl-label">
                Expense Name
              </label>
            </div>

            {isMasterCategory && isSalarySelected ? (
              <div className="field fl-field">
                <select
                  id="misc-employee-name"
                  className="fl-input input-field"
                  value={form.employeeName}
                  onChange={(e) => setForm((prev) => ({ ...prev, employeeName: e.target.value }))}
                  style={{ paddingTop: "14px" }}
                  required
                  disabled={!hasEmployeeOptions}
                >
                  <option value="">
                    {hasEmployeeOptions ? "Select employee name" : "Add Employee Master first"}
                  </option>
                  {employeeNameOptions.map((employeeName) => (
                    <option key={employeeName} value={employeeName}>
                      {employeeName}
                    </option>
                  ))}
                </select>
                <label htmlFor="misc-employee-name" className="fl-label">
                  Employee Name
                </label>
              </div>
            ) : null}

            <div className="field fl-field">
              <input
                id="misc-amount"
                className="fl-input input-field"
                type="number"
                step="any"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="misc-amount" className="fl-label">
                {isSalarySelected ? "Salary Amount" : "Amount"}
              </label>
            </div>

            <div className="field full fl-field">
              <textarea
                id="misc-notes"
                className="fl-input input-field"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder=" "
              />
              <label htmlFor="misc-notes" className="fl-label">
                Notes (Optional)
              </label>
            </div>
          </div>

          <div className="form-actions">
            {editingId ? (
              <>
                <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                  Cancel Edit
                </button>
                <button type="submit" className="btn-primary" disabled={saving || !canSubmit}>
                  {saving ? "Updating..." : "Update Expense"}
                </button>
              </>
            ) : (
              <button type="submit" className="btn-primary" disabled={saving || !canSubmit}>
                {saving ? "Saving..." : isSalarySelected ? "Save Salary" : "Save Expense"}
              </button>
            )}
          </div>
          {!hasSelectableExpenseNames && !isMasterCategory ? (
            <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309" }}>
              Add expense names in Expense Master before saving expenses.
            </div>
          ) : null}
          {isSalarySelected && !hasEmployeeOptions ? (
            <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309" }}>
              Add employee names in Employee Master before saving salary entries.
            </div>
          ) : null}
        </form>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none", gap: 12, flexWrap: "wrap" }}>
          <h2 className="card-title">Saved {title}</h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>From</label>
              <input
                type="date"
                className="input-field"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                style={{ width: 160 }}
                title="Filter from date"
              />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>To</label>
              <input
                type="date"
                className="input-field"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                style={{ width: 160 }}
                title="Filter to date"
              />
            </div>
            <input
              type="search"
              className="input-field"
              placeholder={isMasterCategory ? "Search expense / employee / notes..." : "Search expense / notes..."}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 240 }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setDateFromFilter("");
                setDateToFilter("");
                setQuery("");
              }}
            >
              Clear Filters
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        <div style={{ padding: "0 16px 12px" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                minWidth: 140,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b" }}>Entries</div>
              <div style={{ fontWeight: 800 }}>{totals.count}</div>
            </div>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                minWidth: 140,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b" }}>Dates</div>
              <div style={{ fontWeight: 800 }}>{totals.dateCount}</div>
            </div>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #bbf7d0",
                background: "#f0fdf4",
                minWidth: 180,
              }}
            >
              <div style={{ fontSize: 12, color: "#166534" }}>Total Amount</div>
              <div style={{ fontWeight: 800, color: "#166534" }}>{formatMoney(totals.totalAmount)}</div>
            </div>
          </div>
        </div>

        {dateWiseTotals.length > 0 ? (
          <div style={{ padding: "0 16px 8px" }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: "#334155" }}>Date-wise Totals</div>
            <div className="entries-table-container" style={{ margin: 0, padding: 12 }}>
              <table className="entries-table" style={{ minWidth: 480 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-right">Entries</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {dateWiseTotals.slice(0, 10).map((row) => (
                    <tr key={row.date}>
                      <td>{formatDisplayDate(row.date)}</td>
                      <td className="text-right">{row.count}</td>
                      <td className="text-right">{formatMoney(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: isMasterCategory ? 980 : 860 }}>
            <thead>
              <tr>
                <th style={{ width: 130 }}>Date</th>
                <th style={{ width: 220 }}>Expense</th>
                {isMasterCategory ? <th style={{ width: 220 }}>Employee Name</th> : null}
                <th>Notes</th>
                <th className="text-right" style={{ width: 140 }}>Amount</th>
                <th style={{ width: 170 }} className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={isMasterCategory ? 6 : 5} className="empty-state" style={{ padding: 16 }}>
                    No expenses found
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDisplayDate(entry.date)}</td>
                    <td>{entry.title}</td>
                    {isMasterCategory ? <td>{entry.employeeName || "-"}</td> : null}
                    <td>{entry.notes || "-"}</td>
                    <td className="text-right" style={{ fontWeight: 700 }}>
                      {formatMoney(entry.amount)}
                    </td>
                    <td className="text-right">
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => startEdit(entry)}
                          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                          disabled={!!deleting[entry.id]}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => handleDelete(entry)}
                          style={{ width: 32, height: 32, padding: 0 }}
                          disabled={!!deleting[entry.id]}
                        >
                          x
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
